import type { Feature, Polygon } from 'geojson';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { centroid } from '@turf/turf';

import useBoundingBox from 'components/Map/hooks/useBoundingBox';
import useDTO from 'components/Map/hooks/useDTO';
import useMapContext from 'components/Map/hooks/useMapContext';

import { GEOJSON_BOUNDARY_TYPE, GEOJSON_PROPERTY, MAP_LAYER } from './constants';
import type { BoundaryPartData } from './types';

const useStaticGeometry = () => {
    const { features } = useMapContext();
    const { addFeature, removeFeature } = features;

    const { convertFromDTOs } = useDTO();
    const { upsertBoundingBox } = useBoundingBox();

    const [geometryIds, setGeometryIds] = useState<string[]>([]);

    const reset = () => {
        geometryIds.forEach(id => removeFeature(id));
        setGeometryIds([]);
    };

    const initializeStaticGeometry = (parts?: BoundaryPartData[]): void => {
        if (!parts || parts.length === 0) {
            return;
        }

        reset();

        const features = convertFromDTOs(parts);

        features.forEach((feature, partIndex) => {
            feature.properties = {
                ...feature.properties,
                [GEOJSON_PROPERTY.PART_INDEX]: partIndex
            };
        });

        setGeometryIds(features.map(({ id }) => id as string));

        initializeGeometryFeatures(features);
        initializeClusterFeatures(features);
    };

    const initializeGeometryFeatures = (features: Feature<Polygon>[]) => {
        features.forEach(initializeFeature);
    };

    const initializeClusterFeatures = (features: Feature<Polygon>[]) => {
        const applyFeatures = features.filter(
            feature =>
                feature.properties?.[GEOJSON_PROPERTY.BOUNDARY_TYPE] === GEOJSON_BOUNDARY_TYPE.APPLY
        );

        if (applyFeatures.length <= 1) {
            return;
        }

        applyFeatures.forEach(feature => {
            const centerPoint = centroid(feature.geometry as Polygon);

            centerPoint.properties = {
                ...centerPoint.properties,
                [GEOJSON_PROPERTY.REFERENCED_BOUNDARY_ID]: feature.id as string
            };

            centerPoint.id = uuidv4();

            addFeature({
                feature: centerPoint,
                layerName: MAP_LAYER.CLUSTER
            });
        });
    };

    const FEATURE_LAYER_MAP = {
        [GEOJSON_BOUNDARY_TYPE.APPLY]: MAP_LAYER.STATIC_BOUNDARY_FILL,
        [GEOJSON_BOUNDARY_TYPE.NO_APPLY]: MAP_LAYER.STATIC_OBSTACLE_FILL,
        [GEOJSON_BOUNDARY_TYPE.NO_ENTRY]: MAP_LAYER.STATIC_OBSTACLE_FILL
    };

    const initializeFeature = (feature: Feature<Polygon>) => {
        const boundaryType = feature.properties?.[
            GEOJSON_PROPERTY.BOUNDARY_TYPE
        ] as GEOJSON_BOUNDARY_TYPE;

        feature.properties = {
            ...feature.properties,
            [GEOJSON_PROPERTY.IS_STATIC]: true
        };

        addFeature({
            feature,
            layerName: FEATURE_LAYER_MAP[boundaryType]
        });

        upsertBoundingBox(feature);
    };

    return {
        initializeStaticGeometry
    };
};

export default useStaticGeometry;
